import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

const skills = ["Agency Showreels", "Explainer Videos", "Social Media Content", "Paid Ad Animations", "UI/UX Motion", "Brand Animations", "Storyboarding", "Sound Design"];

export default function About() {
  return (
    <div className="min-h-screen pt-20 bg-[#F8F6F3]" data-testid="about-page">
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="max-w-4xl">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1C1C1C]/40 mb-6">§ 04 / About</p>
            <h1 className="text-4xl md:text-6xl font-black tracking-tight text-[#1C1C1C] mb-2">
              The person behind<br />the <em className="not-italic text-[#FF5E3A]">pixels.</em>
            </h1>
            <div className="w-24 h-1 bg-[#FF5E3A] mt-8 mb-10" />
            <p className="text-base md:text-lg leading-relaxed text-[#1C1C1C]/50 mb-6">
              I'm Elliot — a multidisciplinary creative with years of experience working both in-house and freelance. I take projects from initial idea through to final delivery, including creative direction, design, animation, editing and delivery.
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#1C1C1C]/50 mb-10">
              I've worked with agencies, businesses and established brands and have experience managing clients and projects directly. The tone should feel approachable and confident — like working with a talented freelancer, not a large faceless agency.
            </p>
            <Link to="/contact">
              <button className="bg-[#FF5E3A] text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-[#1C1C1C] transition-colors duration-300" data-testid="about-contact-btn">
                Let's Work Together →
              </button>
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#1C1C1C]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp} className="order-2 lg:order-1 grid grid-cols-2 gap-4">
              <div className="aspect-square overflow-hidden rounded-2xl">
                <img src="https://static.wixstatic.com/media/0564fd_a9f18c99164148ba90f7fc5e72af41b0~mv2.jpg/v1/fill/w_500,h_500,fp_0.66_0.17,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/37002570_1753603594735500_2009197473574158336_n.jpg" alt="Elliot travelling" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-square overflow-hidden rounded-2xl mt-8">
                <img src="https://customer-assets.emergentagent.com/job_d3a7d4a4-e1fc-4216-9bcc-f217303a550b/artifacts/n23qoz2g_WhatsApp%20Image%202025-07-12%20at%2015_03_01.jpeg.avif" alt="Family" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-square overflow-hidden rounded-2xl col-span-2">
                <img src="https://static.wixstatic.com/media/0564fd_3acb209e07e540fb8a5641fcd0d392bd~mv2.jpeg/v1/fill/w_500,h_500,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/WhatsApp%20Image%202024-09-25%20at%2016_12_51%20(1).jpeg" alt="Sydney the dog" className="w-full h-full object-cover" />
              </div>
            </motion.div>
            <motion.div {...fadeUp} className="order-1 lg:order-2">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#FF5E3A] mb-6">Beyond the Work</p>
              <h2 className="text-3xl md:text-5xl font-black tracking-tight text-white mb-8">
                Life outside<br />the <em className="not-italic text-[#FF5E3A]">studio.</em>
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-white/60 mb-6">Away from work, I'm a happy father of two with my partner Emma. Based in Formby, Merseyside, I have my sidekick with me most days — my Olde English Bulldog, Sydney.</p>
              <p className="text-base md:text-lg leading-relaxed text-white/60 mb-6">I love to golf (sometimes!), I'm kind of into running, and I absolutely love to travel the world.</p>
              <p className="text-base md:text-lg leading-relaxed text-white/60">I work with agencies across the UK and beyond — whether you need a single project or ongoing support, I scale to fit your needs.</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="mb-16">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1C1C1C]/40 mb-6">Expertise</p>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight text-[#1C1C1C]">What I <em className="not-italic text-[#FF5E3A]">deliver.</em></h2>
          </motion.div>
          <motion.div {...fadeUp} className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {skills.map((skill, index) => (
              <div key={skill} className="bg-[#F8F6F3] p-6 rounded-2xl flex items-center gap-3 border border-[#E8E5E0] hover:border-[#FF5E3A]/30 transition-colors" data-testid={`skill-${index}`}>
                <CheckCircle size={18} className="text-[#FF5E3A] flex-shrink-0" />
                <span className="font-medium text-sm text-[#1C1C1C]">{skill}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#FF5E3A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-4xl md:text-6xl font-black tracking-tight text-white mb-10">Let's work together.</h2>
            <Link to="/contact">
              <button className="bg-white text-[#1C1C1C] font-semibold text-sm px-10 py-5 rounded-full hover:bg-[#1C1C1C] hover:text-white transition-colors duration-300 inline-flex items-center gap-2" data-testid="about-cta-btn">
                Start a Project <ArrowRight size={14} />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
